import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { GitCompareArrows, ArrowRight } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading } from "@/components/common";

const PHASES = [
  { n: 1, t: "Pure Simulation", d: "Entire OIM validated with no plant connectivity." },
  { n: 2, t: "Shadow Mode", d: "Live data enters the UODL; simulation runs in parallel for comparison." },
  { n: 3, t: "Hybrid Validation", d: "Live vs simulated — physics models tuned." },
  { n: 4, t: "Production Cutover", d: "Simulation disabled; live plant is the primary source." },
  { n: 5, t: "Digital Plant Retained", d: "Training & scenario instance kept permanently." },
];

export default function ShadowComparison() {
  const { data, refresh } = usePoll(endpoints.shadow, 5000);
  if (!data) return <Loading />;

  const isShadow = data.mode === "Shadow";
  const rows = data.shadow?.comparison || [];

  return (
    <div data-testid="shadow-comparison-page">
      <PageHeader icon={GitCompareArrows} title="Shadow Comparison"
        subtitle="Commissioning view — simulated digital-plant model vs live plant data, flowing through one UODL."
        right={
          <span className={`text-xs font-semibold px-3 py-1.5 rounded ${isShadow ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
            Mode: {data.mode}
          </span>
        } />

      {!isShadow ? (
        <Panel testid="shadow-prompt">
          <div className="text-center py-8">
            <GitCompareArrows size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-600 mb-4">Shadow mode runs the live plant feed and the simulation model in parallel so you can validate the model before production cutover.</p>
            <button onClick={async () => { await endpoints.setMode("Shadow"); refresh(); }} data-testid="enable-shadow-btn"
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-700 transition-colors">
              Enable Shadow Mode <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
          <Panel testid="shadow-table" title="Model vs Live" subtitle="Deviation reveals where physics models need tuning">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="text-left font-semibold py-2">Metric</th>
                  <th className="text-right font-semibold">Model</th>
                  <th className="text-right font-semibold">Live</th>
                  <th className="text-right font-semibold">Deviation</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.metric} className="border-b border-slate-50">
                    <td className="py-2.5 font-medium text-slate-800">{r.metric}</td>
                    <td className="text-right font-mono text-slate-500">{r.model}</td>
                    <td className="text-right font-mono font-semibold text-slate-900">{r.live}</td>
                    <td className={`text-right font-mono font-semibold ${Math.abs(r.deviation_pct) > 3 ? "text-red-600" : Math.abs(r.deviation_pct) > 1 ? "text-amber-600" : "text-emerald-600"}`}>
                      {r.deviation_pct > 0 ? "+" : ""}{r.deviation_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel testid="shadow-chart" title="Deviation by Metric" subtitle="% live vs model">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={rows} layout="vertical" margin={{ left: 20, right: 20 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="metric" width={130} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => v + "%"} />
                <Bar dataKey="deviation_pct" radius={[0, 3, 3, 0]} isAnimationActive={false}>
                  {rows.map((r, i) => (
                    <Cell key={i} fill={Math.abs(r.deviation_pct) > 3 ? "#ef4444" : Math.abs(r.deviation_pct) > 1 ? "#f59e0b" : "#10b981"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}

      <Panel testid="commissioning-phases" title="Commissioning Strategy" subtitle="One runtime, multiple data sources (Principle 8)">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          {PHASES.map((p) => (
            <div key={p.n} className="border border-slate-100 rounded p-3">
              <div className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center mb-2">{p.n}</div>
              <div className="text-[13px] font-semibold text-slate-800">{p.t}</div>
              <div className="text-[11px] text-slate-500 mt-1">{p.d}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
